import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Activity, Loader2, Save, LineChart, Target, Grid3x3, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SELECT = 'h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[150px]';

export default function AdvancedStatsPanel({ userId, projectId, refreshKey = 0, onAssetsChanged,
  onOpenChapter }) {
  const [datasets, setDatasets] = useState([]);
  const [dsId, setDsId] = useState('');
  // survival
  const [timeCol, setTimeCol] = useState('');
  const [eventCol, setEventCol] = useState('');
  const [groupCol, setGroupCol] = useState('');
  const [horizons, setHorizons] = useState('1, 3, 5');
  const [style, setStyle] = useState('colour');
  const [makeFigure, setMakeFigure] = useState(true);
  const [riskTable, setRiskTable] = useState(true);
  const [kmRunning, setKmRunning] = useState(false);
  const [km, setKm] = useState(null);
  // regression
  const [model, setModel] = useState('linear');
  const [outcome, setOutcome] = useState('');
  const [predictors, setPredictors] = useState([]);
  const [regRunning, setRegRunning] = useState(false);
  const [reg, setReg] = useState(null);
  // roc
  const [rocOutcome, setRocOutcome] = useState('');
  const [rocPredictors, setRocPredictors] = useState([]);
  const [rocStyle, setRocStyle] = useState('colour');
  const [rocFigure, setRocFigure] = useState(true);
  const [rocRunning, setRocRunning] = useState(false);
  const [roc, setRoc] = useState(null);
  // correlation
  const [corrCols, setCorrCols] = useState([]);
  const [corrMethod, setCorrMethod] = useState('pearson');
  const [corrFigure, setCorrFigure] = useState(false);
  const [corrRunning, setCorrRunning] = useState(false);
  const [corr, setCorr] = useState(null);
  const [methodsRunning, setMethodsRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await thesisApi.listDatasets(userId, projectId);
      setDatasets(rows);
      if (rows.length && !rows.find((d) => d.id === dsId)) setDsId(rows[0].id);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, projectId, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const active = datasets.find((d) => d.id === dsId);
  const numeric = (active?.columns || []).filter((c) => c.kind === 'numeric');
  const categorical = (active?.columns || []).filter((c) => c.kind === 'categorical' && c.levels <= 6);
  const allCols = active?.columns || [];

  const runSurvival = async () => {
    if (!timeCol || !eventCol) { toast.error('Pick the time and event columns'); return; }
    setKmRunning(true);
    try {
      const data = await thesisApi.datasetSurvival(userId, dsId, {
        time_col: timeCol, event_col: eventCol, group_col: groupCol || null,
        horizons: horizons.split(',').map((h) => parseFloat(h.trim())).filter((h) => !Number.isNaN(h)),
        make_figure: makeFigure, style, risk_table: riskTable,
      });
      setKm(data);
      toast.success(data.figure
        ? 'Kaplan-Meier computed — the curve was added to your figures'
        : 'Kaplan-Meier computed');
      if (data.figure) onAssetsChanged?.();
    } catch (e) {
      toast.error('Survival analysis failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setKmRunning(false);
    }
  };

  const runRegression = async () => {
    if (model !== 'cox' && !outcome) { toast.error('Pick the outcome variable'); return; }
    if (model === 'cox' && (!timeCol || !eventCol)) { toast.error('Cox needs the time and event columns (set above)'); return; }
    if (!predictors.length) { toast.error('Pick at least one predictor'); return; }
    setRegRunning(true);
    try {
      const data = await thesisApi.datasetRegression(userId, dsId, {
        model, outcome, predictors,
        time_col: model === 'cox' ? timeCol : null,
        event_col: model === 'cox' ? eventCol : null,
        group_col: model === 'cox' ? (groupCol || null) : null,
        ph_check: model === 'cox',
        ph_figure: model === 'cox',
      });
      setReg(data);
      if (data.ph?.violated?.length) {
        toast.warning(`Model fitted — proportional hazards violated for: ${data.ph.violated.join(', ')}`);
      } else {
        toast.success('Model fitted');
      }
      if (data.ph_figure) onAssetsChanged?.();
    } catch (e) {
      toast.error('Regression failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRegRunning(false);
    }
  };

  const runRoc = async () => {
    if (!rocOutcome) { toast.error('Pick the binary outcome (e.g. disease yes/no)'); return; }
    if (!rocPredictors.length) { toast.error('Pick at least one continuous predictor'); return; }
    setRocRunning(true);
    try {
      const data = await thesisApi.datasetRoc(userId, dsId, {
        outcome: rocOutcome, predictors: rocPredictors,
        make_figure: rocFigure, style: rocStyle,
      });
      setRoc(data);
      toast.success(data.figure ? 'ROC computed — curve added to your figures' : 'ROC computed');
      if (data.figure) onAssetsChanged?.();
    } catch (e) {
      toast.error('ROC failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRocRunning(false);
    }
  };

  const runCorrelation = async () => {
    if (corrCols.length < 2) { toast.error('Pick at least two continuous variables'); return; }
    setCorrRunning(true);
    try {
      const data = await thesisApi.datasetCorrelation(userId, dsId, {
        columns: corrCols, method: corrMethod, make_figure: corrFigure, style: 'colour',
      });
      setCorr(data);
      toast.success(data.figure ? 'Matrix computed — heatmap added to your figures' : 'Matrix computed');
      if (data.figure) onAssetsChanged?.();
    } catch (e) {
      toast.error('Correlation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCorrRunning(false);
    }
  };

  const handleMethodsParagraph = async () => {
    setMethodsRunning(true);
    try {
      const res = await thesisApi.statisticsMethodsParagraph(userId, projectId, null);
      toast.success(res.mode === 'new'
        ? 'Added a "Statistical analysis" paragraph to a new Methods section'
        : 'Added a "Statistical analysis" paragraph to the section');
      onAssetsChanged?.();
      if (res.chapter_id) await onOpenChapter?.(res.chapter_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setMethodsRunning(false);
    }
  };

  const saveAsTable = async (result) => {
    setSaving(true);
    try {
      const t = await thesisApi.saveTable(userId, projectId, {
        caption: result.caption, markdown: result.markdown, note: result.note, source: 'statistics',
      });
      toast.success(`Saved as Table ${t.number}`);
      onAssetsChanged?.();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!datasets.length) {
    return (
      <Card data-testid="advanced-stats-panel">
        <CardHeader>
          <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            <Activity className="w-5 h-5 text-[#1d4ed8]" /> Survival, ROC &amp; regression
            <Badge variant="outline" className="text-[10px]">needs a dataset</Badge>
          </CardTitle>
          <CardDescription>
            Upload a CSV or Excel file with your study data above and this panel unlocks
            Kaplan-Meier curves with a number-at-risk table, Cox models with a
            proportional-hazards check, ROC/AUC and correlation matrices.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card data-testid="advanced-stats-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Activity className="w-5 h-5 text-[#1d4ed8]" /> Survival, ROC &amp; regression
          <Badge variant="outline" className="text-[10px]">Kaplan-Meier · Cox · ROC/AUC · correlation</Badge>
        </CardTitle>
        <CardDescription>
          Kaplan-Meier estimates with log-rank testing, diagnostic accuracy (AUC with 95% CI and
          Youden cut-off), correlation matrices, and multivariable models reported as β / OR / HR
          with 95% confidence intervals. Curves and heatmaps are saved straight into your figures.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <select className={SELECT} value={dsId}
          onChange={(e) => { setDsId(e.target.value); setKm(null); setReg(null); }}
          data-testid="adv-dataset-select">
          {datasets.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.n_rows} rows)</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={handleMethodsParagraph}
          disabled={methodsRunning} data-testid="stats-methods-paragraph-btn">
          {methodsRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
          Add "Statistical analysis" to Methods
        </Button>

        {/* SURVIVAL */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Kaplan-Meier survival
          </div>
          <div className="flex flex-wrap gap-2">
            <select className={SELECT} value={timeCol} onChange={(e) => setTimeCol(e.target.value)}
              data-testid="km-time-select">
              <option value="">Time column…</option>
              {numeric.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select className={SELECT} value={eventCol} onChange={(e) => setEventCol(e.target.value)}
              data-testid="km-event-select">
              <option value="">Event column (1 = event)…</option>
              {allCols.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select className={SELECT} value={groupCol} onChange={(e) => setGroupCol(e.target.value)}
              data-testid="km-group-select">
              <option value="">No group comparison</option>
              {categorical.map((c) => <option key={c.name} value={c.name}>Compare by: {c.name}</option>)}
            </select>
            <Input className="w-36" value={horizons} onChange={(e) => setHorizons(e.target.value)}
              placeholder="1, 3, 5" data-testid="km-horizons-input" />
            <select className={SELECT} value={style} onChange={(e) => setStyle(e.target.value)}
              data-testid="km-style-select">
              <option value="colour">Curve: colour</option>
              <option value="bw">Curve: black &amp; white (print)</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={makeFigure} onChange={(e) => setMakeFigure(e.target.checked)}
                className="accent-teal-700" data-testid="km-make-figure-toggle" />
              Add the curve to my figures
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={riskTable} onChange={(e) => setRiskTable(e.target.checked)}
                className="accent-teal-700" data-testid="km-risk-table-toggle" />
              Number-at-risk table under the curve
            </label>
            <Button onClick={runSurvival} disabled={kmRunning} data-testid="km-run-btn">
              {kmRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Computing...</>
                : <><LineChart className="w-4 h-4 mr-2" />Run Kaplan-Meier</>}
            </Button>
          </div>

          {km && (
            <div className="space-y-2" data-testid="km-result">
              <div className="text-sm font-medium">{km.caption}</div>
              <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56">{km.markdown}</pre>
              <p className="text-[11px] text-muted-foreground italic">{km.note}</p>
              {km.figure && (
                <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">
                  Curve saved as Figure {km.figure.number} — insert it from the Figures panel above.
                </p>
              )}
              <Button variant="outline" onClick={() => saveAsTable(km)} disabled={saving}
                data-testid="km-save-table-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save as numbered table
              </Button>
            </div>
          )}
        </div>

        {/* REGRESSION */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Regression model
          </div>
          <div className="flex flex-wrap gap-2">
            <select className={SELECT} value={model} onChange={(e) => { setModel(e.target.value); setReg(null); }}
              data-testid="reg-model-select">
              <option value="linear">Linear (β)</option>
              <option value="logistic">Logistic (OR)</option>
              <option value="cox">Cox proportional hazards (HR)</option>
            </select>
            {model !== 'cox' && (
              <select className={SELECT} value={outcome} onChange={(e) => setOutcome(e.target.value)}
                data-testid="reg-outcome-select">
                <option value="">Outcome variable…</option>
                {(model === 'logistic' ? allCols : numeric).map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
            <Button onClick={runRegression} disabled={regRunning} data-testid="reg-run-btn">
              {regRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fitting...</>
                : 'Fit model'}
            </Button>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              Predictors {model === 'cox' && '(Cox uses the time / event columns selected above)'}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="reg-predictors">
              {allCols.filter((c) => c.name !== outcome).map((c) => {
                const on = predictors.includes(c.name);
                return (
                  <button key={c.name} type="button"
                    onClick={() => setPredictors((p) => (on ? p.filter((x) => x !== c.name) : [...p, c.name]))}
                    className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${on
                      ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                      : 'bg-background border-input hover:border-[#1d4ed8]/40'}`}
                    data-testid={`reg-predictor-${c.name}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          {reg && (
            <div className="space-y-2" data-testid="reg-result">
              <div className="text-sm font-medium">{reg.caption}</div>
              <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56">{reg.markdown}</pre>
              <p className="text-[11px] text-muted-foreground italic">{reg.note}</p>
              <Button variant="outline" onClick={() => saveAsTable(reg)} disabled={saving}
                data-testid="reg-save-table-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save as numbered table
              </Button>

              {reg.ph && (
                <div className="mt-3 rounded-md border border-border p-3 space-y-2" data-testid="cox-ph-result">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">Proportional-hazards assumption</span>
                    <Badge variant="outline"
                      className={reg.ph.violated?.length
                        ? 'text-[10px] border-amber-500 text-amber-700 dark:text-amber-400'
                        : 'text-[10px] border-emerald-600 text-emerald-700 dark:text-emerald-400'}
                      data-testid="cox-ph-badge">
                      {reg.ph.violated?.length
                        ? `Violated: ${reg.ph.violated.join(', ')}`
                        : 'Assumption held'}
                    </Badge>
                  </div>
                  <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56">{reg.ph.markdown}</pre>
                  <p className="text-[11px] text-muted-foreground italic">{reg.ph.note}</p>
                  {reg.ph_figure && (
                    <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">
                      Schoenfeld-residual / log-log diagnostics saved as Figure {reg.ph_figure.number}.
                    </p>
                  )}
                  {reg.ph_figure_error && (
                    <p className="text-[11px] text-amber-600">Diagnostics plot failed: {reg.ph_figure_error}</p>
                  )}
                  <Button variant="outline" size="sm" onClick={() => saveAsTable(reg.ph)} disabled={saving}
                    data-testid="cox-ph-save-table-btn">
                    <Save className="w-4 h-4 mr-2" /> Save assumption check as table
                  </Button>
                </div>
              )}
              {reg.ph_error && (
                <p className="text-[11px] text-amber-600">Assumption check failed: {reg.ph_error}</p>
              )}
            </div>
          )}
        </div>
        {/* ROC / AUC */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Diagnostic accuracy (ROC / AUC)
          </div>
          <div className="flex flex-wrap gap-2">
            <select className={SELECT} value={rocOutcome} onChange={(e) => setRocOutcome(e.target.value)}
              data-testid="roc-outcome-select">
              <option value="">Binary outcome…</option>
              {allCols.filter((c) => c.levels === 2).map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
            <select className={SELECT} value={rocStyle} onChange={(e) => setRocStyle(e.target.value)}
              data-testid="roc-style-select">
              <option value="colour">Curve: colour</option>
              <option value="bw">Curve: black &amp; white (print)</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={rocFigure} onChange={(e) => setRocFigure(e.target.checked)}
                className="accent-teal-700" data-testid="roc-make-figure-toggle" />
              Add the ROC curve to my figures
            </label>
            <Button onClick={runRoc} disabled={rocRunning} data-testid="roc-run-btn">
              {rocRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Computing...</>
                : <><Target className="w-4 h-4 mr-2" />Run ROC</>}
            </Button>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">Test variables (continuous)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="roc-predictors">
              {numeric.map((c) => {
                const on = rocPredictors.includes(c.name);
                return (
                  <button key={c.name} type="button"
                    onClick={() => setRocPredictors((p) => (on ? p.filter((x) => x !== c.name) : [...p, c.name]))}
                    className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${on
                      ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                      : 'bg-background border-input hover:border-[#1d4ed8]/40'}`}
                    data-testid={`roc-predictor-${c.name}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
          {roc && (
            <div className="space-y-2" data-testid="roc-result">
              <div className="text-sm font-medium">{roc.caption}</div>
              <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56">{roc.markdown}</pre>
              <p className="text-[11px] text-muted-foreground italic">{roc.note}</p>
              {roc.figure && (
                <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">
                  Curve saved as Figure {roc.figure.number} — insert it from the Figures panel.
                </p>
              )}
              <Button variant="outline" onClick={() => saveAsTable(roc)} disabled={saving}
                data-testid="roc-save-table-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save as numbered table
              </Button>
            </div>
          )}
        </div>

        {/* CORRELATION MATRIX */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Correlation matrix
          </div>
          <div className="flex flex-wrap gap-2">
            <select className={SELECT} value={corrMethod} onChange={(e) => setCorrMethod(e.target.value)}
              data-testid="corr-method-select">
              <option value="pearson">Pearson r</option>
              <option value="spearman">Spearman rho</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={corrFigure} onChange={(e) => setCorrFigure(e.target.checked)}
                className="accent-teal-700" data-testid="corr-make-figure-toggle" />
              Also create a heatmap figure
            </label>
            <Button onClick={runCorrelation} disabled={corrRunning} data-testid="corr-run-btn">
              {corrRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Computing...</>
                : <><Grid3x3 className="w-4 h-4 mr-2" />Build matrix</>}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="corr-columns">
            {numeric.map((c) => {
              const on = corrCols.includes(c.name);
              return (
                <button key={c.name} type="button"
                  onClick={() => setCorrCols((p) => (on ? p.filter((x) => x !== c.name) : [...p, c.name]))}
                  className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${on
                    ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                    : 'bg-background border-input hover:border-[#1d4ed8]/40'}`}
                  data-testid={`corr-column-${c.name}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
          {corr && (
            <div className="space-y-2" data-testid="corr-result">
              <div className="text-sm font-medium">{corr.caption}</div>
              <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56">{corr.markdown}</pre>
              <p className="text-[11px] text-muted-foreground italic">{corr.note}</p>
              <Button variant="outline" onClick={() => saveAsTable(corr)} disabled={saving}
                data-testid="corr-save-table-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save as numbered table
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
